import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getSupabaseClient } from '@/lib/supabase';
import type { Company } from '@/hooks/useAuth';

interface FormErrors {
  company_name?: string;
  country?: string;
  contact_email?: string;
  website?: string;
}

const businessTypes = [
  'Manufacturer',
  'Distributor / Wholesaler',
  'Retailer',
  'Brand Owner',
  'Other',
];

const companySizes = [
  '1-10 employees',
  '11-50 employees',
  '51-200 employees',
  '201-1000 employees',
  '1000+ employees',
];

function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
}

function isValidEmail(email: string): boolean {
  if (!email) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isValidUrl(url: string): boolean {
  if (!url) return true;
  return /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(url);
}

export default function CompanyPage() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const { user, company: authCompany, updateCompany, isLoading: authLoading } = useAuth();

  const [form, setForm] = useState<Partial<Company>>({
    company_name: '',
    country: '',
    business_type: '',
    website: '',
    contact_email: '',
    contact_phone: '',
    tax_id: '',
    company_size: '',
  });
  const [originalForm, setOriginalForm] = useState<Partial<Company>>({
    company_name: '',
    country: '',
    business_type: '',
    website: '',
    contact_email: '',
    contact_phone: '',
    tax_id: '',
    company_size: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Fetch company data directly as fallback if auth context hasn't loaded yet
  const fetchCompany = useCallback(async () => {
    if (!user) return;
    const supabase = getSupabaseClient();
    if (!supabase) return;

    const { data, error } = await supabase
      .from('customer_companies')
      .select('*')
      .eq('owner_user_id', user.id)
      .maybeSingle();

    if (!error && data) {
      const loaded: Partial<Company> = {
        company_name: data.company_name || '',
        country: data.country || '',
        business_type: data.business_type || '',
        website: data.website || '',
        contact_email: data.contact_email || '',
        contact_phone: data.contact_phone || '',
        tax_id: data.tax_id || '',
        company_size: data.company_size || '',
        status: data.status,
        created_at: data.created_at,
        updated_at: data.updated_at,
      };
      setForm(loaded);
      setOriginalForm(loaded);
    }
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/sign-in');
      return;
    }
    if (authCompany) {
      const loaded: Partial<Company> = {
        company_name: authCompany.company_name || '',
        country: authCompany.country || '',
        business_type: authCompany.business_type || '',
        website: authCompany.website || '',
        contact_email: authCompany.contact_email || '',
        contact_phone: authCompany.contact_phone || '',
        tax_id: authCompany.tax_id || '',
        company_size: authCompany.company_size || '',
        status: authCompany.status,
        created_at: authCompany.created_at,
        updated_at: authCompany.updated_at,
      };
      setForm(loaded);
      setOriginalForm(loaded);
      setIsLoading(false);
    } else if (!authLoading && user) {
      fetchCompany();
    }
  }, [authLoading, user, authCompany, navigate, fetchCompany]);

  const handleChange = (field: keyof Company, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    setErrors((prev) => {
      const next = { ...prev };
      if (field in next) delete next[field as keyof FormErrors];
      return next;
    });
    // Clear messages on input
    setSuccessMessage('');
    setErrorMessage('');
  };

  const validate = (): boolean => {
    const nextErrors: FormErrors = {};
    if (!form.company_name?.trim()) {
      nextErrors.company_name = t('companyProfile.validation.required');
    }
    if (!form.country?.trim()) {
      nextErrors.country = t('companyProfile.validation.required');
    }
    if (form.contact_email && !isValidEmail(form.contact_email)) {
      nextErrors.contact_email = t('companyProfile.validation.email');
    }
    if (form.website && !isValidUrl(form.website)) {
      nextErrors.website = t('companyProfile.validation.url');
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    if (!validate()) return;

    setIsSaving(true);
    try {
      const payload = {
        company_name: form.company_name?.trim() || null,
        country: form.country?.trim() || null,
        business_type: form.business_type?.trim() || null,
        website: form.website?.trim() || null,
        contact_email: form.contact_email?.trim() || null,
        contact_phone: form.contact_phone?.trim() || null,
        tax_id: form.tax_id?.trim() || null,
        company_size: form.company_size?.trim() || null,
      };

      await updateCompany(payload);
      setSuccessMessage(t('companyProfile.successMessage'));
      setOriginalForm({ ...form });
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.includes('not authenticated') || msg.includes('Not authenticated')) {
        setErrorMessage('Session expired. Please sign in again.');
        navigate('/sign-in');
      } else {
        setErrorMessage(t('companyProfile.errorMessage'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges =
    originalForm &&
    (form.company_name !== originalForm.company_name ||
      form.country !== originalForm.country ||
      form.business_type !== originalForm.business_type ||
      form.website !== originalForm.website ||
      form.contact_email !== originalForm.contact_email ||
      form.contact_phone !== originalForm.contact_phone ||
      form.tax_id !== originalForm.tax_id ||
      form.company_size !== originalForm.company_size);

  const status = form.status || 'active';
  const statusColor =
    status === 'active'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : status === 'suspended'
        ? 'bg-rose-50 text-rose-700 border-rose-200'
        : 'bg-amber-50 text-amber-700 border-amber-200';

  if (isLoading || authLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center gap-2 text-ink-400">
          <i className="ri-loader-4-line animate-spin text-xl" />
          <span className="text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-xl md:text-2xl font-bold text-ink-700">{t('companyProfile.pageTitle')}</h1>
        <p className="text-sm text-ink-300 mt-1">{t('companyProfile.subtitle')}</p>
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="mb-4 md:mb-6 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-sm text-emerald-700 animate-[fadeIn_0.2s_ease-out]">
          <i className="ri-check-line text-lg" />
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="mb-4 md:mb-6 p-3 bg-rose-50 border border-rose-200 rounded-lg flex items-center gap-2 text-sm text-rose-700 animate-[fadeIn_0.2s_ease-out]">
          <i className="ri-error-warning-line text-lg" />
          {errorMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
        {/* Section 1: Company Information */}
        <div className="rounded-xl border border-ink-100 bg-white overflow-hidden">
          <div className="px-4 md:px-5 py-3 md:py-4 border-b border-ink-100 bg-stone-50/50">
            <div className="flex items-center gap-2">
              <i className="ri-building-4-line text-warm-400 text-lg" />
              <h2 className="text-sm md:text-base font-semibold text-ink-600">
                {t('companyProfile.section.companyInfo')}
              </h2>
            </div>
          </div>
          <div className="px-4 md:px-5 py-4 md:py-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Company Name */}
            <div>
              <label className="block text-xs font-medium text-ink-500 mb-1.5">
                {t('companyProfile.field.companyName')}
                <span className="text-rose-500 ml-0.5">*</span>
              </label>
              <input
                type="text"
                value={form.company_name || ''}
                onChange={(e) => handleChange('company_name', e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-colors ${
                  errors.company_name
                    ? 'border-rose-300 focus:border-rose-500 bg-rose-50/30'
                    : 'border-ink-200 focus:border-amber-500'
                }`}
                placeholder="Acme Packaging Co."
              />
              {errors.company_name && (
                <p className="mt-1 text-xs text-rose-600">{errors.company_name}</p>
              )}
            </div>

            {/* Country */}
            <div>
              <label className="block text-xs font-medium text-ink-500 mb-1.5">
                {t('companyProfile.field.country')}
                <span className="text-rose-500 ml-0.5">*</span>
              </label>
              <input
                type="text"
                value={form.country || ''}
                onChange={(e) => handleChange('country', e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-colors ${
                  errors.country
                    ? 'border-rose-300 focus:border-rose-500 bg-rose-50/30'
                    : 'border-ink-200 focus:border-amber-500'
                }`}
                placeholder="United States"
              />
              {errors.country && (
                <p className="mt-1 text-xs text-rose-600">{errors.country}</p>
              )}
            </div>

            {/* Business Type */}
            <div>
              <label className="block text-xs font-medium text-ink-500 mb-1.5">
                {t('companyProfile.field.businessType')}
              </label>
              <select
                value={form.business_type || ''}
                onChange={(e) => handleChange('business_type', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-ink-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors bg-white"
              >
                <option value="">{t('companyProfile.selectPlaceholder')}</option>
                {businessTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Website */}
            <div>
              <label className="block text-xs font-medium text-ink-500 mb-1.5">
                {t('companyProfile.field.website')}
              </label>
              <input
                type="text"
                value={form.website || ''}
                onChange={(e) => handleChange('website', e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-colors ${
                  errors.website
                    ? 'border-rose-300 focus:border-rose-500 bg-rose-50/30'
                    : 'border-ink-200 focus:border-amber-500'
                }`}
                placeholder="https://example.com"
              />
              {errors.website && (
                <p className="mt-1 text-xs text-rose-600">{errors.website}</p>
              )}
            </div>
          </div>
        </div>

        {/* Section 2: Contact Information */}
        <div className="rounded-xl border border-ink-100 bg-white overflow-hidden">
          <div className="px-4 md:px-5 py-3 md:py-4 border-b border-ink-100 bg-stone-50/50">
            <div className="flex items-center gap-2">
              <i className="ri-contacts-line text-warm-400 text-lg" />
              <h2 className="text-sm md:text-base font-semibold text-ink-600">
                {t('companyProfile.section.contactInfo')}
              </h2>
            </div>
          </div>
          <div className="px-4 md:px-5 py-4 md:py-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Contact Email */}
            <div>
              <label className="block text-xs font-medium text-ink-500 mb-1.5">
                {t('companyProfile.field.contactEmail')}
              </label>
              <input
                type="email"
                value={form.contact_email || ''}
                onChange={(e) => handleChange('contact_email', e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-colors ${
                  errors.contact_email
                    ? 'border-rose-300 focus:border-rose-500 bg-rose-50/30'
                    : 'border-ink-200 focus:border-amber-500'
                }`}
                placeholder="contact@company.com"
              />
              {errors.contact_email && (
                <p className="mt-1 text-xs text-rose-600">{errors.contact_email}</p>
              )}
            </div>

            {/* Contact Phone */}
            <div>
              <label className="block text-xs font-medium text-ink-500 mb-1.5">
                {t('companyProfile.field.contactPhone')}
              </label>
              <input
                type="tel"
                value={form.contact_phone || ''}
                onChange={(e) => handleChange('contact_phone', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-ink-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors"
                placeholder="+1 (555) 000-0000"
              />
            </div>
          </div>
        </div>

        {/* Section 3: Business Information */}
        <div className="rounded-xl border border-ink-100 bg-white overflow-hidden">
          <div className="px-4 md:px-5 py-3 md:py-4 border-b border-ink-100 bg-stone-50/50">
            <div className="flex items-center gap-2">
              <i className="ri-briefcase-line text-warm-400 text-lg" />
              <h2 className="text-sm md:text-base font-semibold text-ink-600">
                {t('companyProfile.section.businessInfo')}
              </h2>
            </div>
          </div>
          <div className="px-4 md:px-5 py-4 md:py-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Tax ID */}
            <div>
              <label className="block text-xs font-medium text-ink-500 mb-1.5">
                {t('companyProfile.field.taxId')}
              </label>
              <input
                type="text"
                value={form.tax_id || ''}
                onChange={(e) => handleChange('tax_id', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-ink-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors"
                placeholder="US-123456789"
              />
            </div>

            {/* Company Size */}
            <div>
              <label className="block text-xs font-medium text-ink-500 mb-1.5">
                {t('companyProfile.field.companySize')}
              </label>
              <select
                value={form.company_size || ''}
                onChange={(e) => handleChange('company_size', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-ink-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors bg-white"
              >
                <option value="">{t('companyProfile.selectPlaceholder')}</option>
                {companySizes.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Section 4: Account Status */}
        <div className="rounded-xl border border-ink-100 bg-white overflow-hidden">
          <div className="px-4 md:px-5 py-3 md:py-4 border-b border-ink-100 bg-stone-50/50">
            <div className="flex items-center gap-2">
              <i className="ri-shield-check-line text-warm-400 text-lg" />
              <h2 className="text-sm md:text-base font-semibold text-ink-600">
                {t('companyProfile.section.accountStatus')}
              </h2>
            </div>
          </div>
          <div className="px-4 md:px-5 py-4 md:py-5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-ink-500">{t('companyProfile.field.status')}</span>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium border ${statusColor}`}
                >
                  {t(`companyProfile.status.${status}`) || status}
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs text-ink-400">
                <span>
                  {t('companyProfile.createdAt')}: {formatDate(form.created_at || null)}
                </span>
                <span className="hidden sm:inline">|</span>
                <span>
                  {t('companyProfile.updatedAt')}: {formatDate(form.updated_at || null)}
                </span>
              </div>
            </div>
            {(status === 'inactive' || status === 'suspended') && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-start gap-2">
                <i className="ri-information-line text-base shrink-0 mt-0.5" />
                {t('companyProfile.statusNotice')}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-2 pb-8">
          <div className="text-xs text-ink-400">
            {hasChanges ? (
              <span className="flex items-center gap-1 text-amber-600">
                <i className="ri-edit-circle-line" />
                Unsaved changes
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <i className="ri-check-line" />
                All changes saved
              </span>
            )}
          </div>
          <button
            type="submit"
            disabled={isSaving || !hasChanges}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:bg-ink-200 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap"
          >
            {isSaving ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                {t('companyProfile.saving')}
              </>
            ) : (
              <>
                <i className="ri-save-line" />
                {t('companyProfile.saveButton')}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}